import type { AccountTenantDto } from '@/services/generated/api';
import { useGetAccountQuery } from './accountApi';
import { useAppSelector } from '@/app/hooks';

export interface ActiveTenant {
  /** `''` while it cannot be resolved, which is what an RTK `skip` reads. */
  tenantId: string;
  /** The membership itself, for whatever needs more than the id. */
  tenant: AccountTenantDto | null;
  isLoading: boolean;
}

/**
 * The organisation a control-plane screen is acting on.
 *
 * The control plane names its tenant in the URL rather than in `X-Tenant-Id`
 * (`/api/account/tenants/:tenantId/…`), so these screens need the id itself —
 * unlike the data plane, where the middleware resolves it from the header.
 *
 * Two sources, in order:
 *
 *  1. What the identity strip has selected, IF it is still a membership. The
 *     slice holds a persisted id that can outlive the membership it names.
 *  2. With exactly one organisation, that one — the slice may never have
 *     chosen, and it is the same organisation the middleware would resolve
 *     from a request with no header.
 *
 * With several organisations and nothing selected it stays `''` rather than
 * guessing: the first of a list is a bad chooser, and acting on the wrong
 * organisation is worse than asking.
 */
export function useActiveTenant(): ActiveTenant {
  const activeTenantId = useAppSelector((state) => state.auth.activeTenantId);
  const { data: account, isLoading } = useGetAccountQuery();

  const tenant =
    account?.tenants.find((t) => t.id === activeTenantId) ??
    (account?.tenants.length === 1 ? (account.tenants[0] ?? null) : null);

  return { tenantId: tenant?.id ?? '', tenant, isLoading };
}
